import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';

export type TimelineTone = 'brand' | 'neutral' | 'good' | 'warning' | 'bad';

export interface TimelineStep {
  key: string;
  /** What happened, in a few words: "Handed to Barbara Blair". */
  title: React.ReactNode;
  /** When, or anything else short that belongs beside the title. */
  meta?: React.ReactNode;
  /** A line of explanation under the title. */
  detail?: React.ReactNode;
  /** What sits in the dot: an initial, a small icon. Empty is fine. */
  marker?: React.ReactNode;
  /** `brand` for the thing the product did, `neutral` for the rest. */
  tone?: TimelineTone;
  /** One control at the end of the row, e.g. "Read transcript". */
  action?: React.ReactNode;
  /** Not happened yet: drawn hollow, with a dashed line leading into it. */
  upcoming?: boolean;
}

export interface TimelineProps {
  steps: TimelineStep[];
  /** The steps are on their way: shimmering ones hold the space. */
  loading?: boolean;
  /**
   * One line a step, smaller markers and less space between: for a long run
   * of like steps — a client's calls — read at a glance rather than one by one.
   */
  dense?: boolean;
}

const MARKER = 24;
const DENSE_MARKER = 16;

/**
 * Things that happened to one subject, in order, top to bottom.
 *
 * For a record's history — who had it, what they did, what comes next — where
 * the order is the point. The last step can be `upcoming`, so the reader sees
 * where it is heading as well as where it has been.
 *
 * Not for a process with fixed stages (use StageFlow) or a list of unrelated
 * events (use ListTable).
 */
export function Timeline({ steps, loading = false, dense = false }: TimelineProps) {
  const marker = dense ? DENSE_MARKER : MARKER;
  if (loading) {
    return (
      <Stack spacing={2} aria-hidden>
        {[70, 55, 80, 60].map((width, i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
            <Skeleton variant="circular" width={MARKER} height={MARKER} sx={{ flexShrink: 0 }} />
            <Stack sx={{ flex: 1 }}>
              <Skeleton variant="text" width={`${width}%`} />
              <Skeleton variant="text" width={`${width - 20}%`} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  }
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        const nextUpcoming = !last && steps[index + 1].upcoming;
        return (
          <Box
            component="li"
            key={step.key}
            data-state={step.upcoming ? 'upcoming' : 'done'}
            sx={{ display: 'grid', gridTemplateColumns: `${marker}px 1fr`, columnGap: dense ? 1 : 1.5 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Marker tone={step.tone || 'neutral'} upcoming={step.upcoming} size={marker} dense={dense}>
                {step.marker}
              </Marker>
              {last ? null : (
                <Box
                  sx={(t) => ({
                    flex: 1,
                    minHeight: dense ? 4 : 12,
                    width: 0,
                    borderLeft: `2px ${nextUpcoming ? 'dashed' : 'solid'} ${t.tokens.border.default}`,
                  })}
                />
              )}
            </Box>

            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ pb: last ? 0 : dense ? 0.75 : 2, minWidth: 0 }}
            >
              <Stack spacing={0.25} sx={{ minWidth: 0, pt: dense ? 0 : 0.25 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    sx={(t) => ({
                      fontWeight: 600,
                      color: step.upcoming ? t.tokens.text.secondary : t.tokens.text.primary,
                    })}
                  >
                    {step.title}
                  </Typography>
                  {step.meta ? (
                    <Typography
                      variant="caption"
                      sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
                    >
                      {step.meta}
                    </Typography>
                  ) : null}
                  {dense && step.detail ? (
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
                      {step.detail}
                    </Typography>
                  ) : null}
                </Stack>
                {!dense && step.detail ? (
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                    {step.detail}
                  </Typography>
                ) : null}
              </Stack>
              {step.action ? <Box sx={{ flexShrink: 0 }}>{step.action}</Box> : null}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function Marker({
  tone,
  upcoming,
  size,
  dense,
  children,
}: {
  tone: TimelineTone;
  upcoming?: boolean;
  size: number;
  dense?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Box
      data-testid="timeline-marker"
      sx={(t) => {
        const ink = {
          brand: t.tokens.brand.primary,
          neutral: t.tokens.text.secondary,
          good: t.tokens.semantic.success,
          warning: t.tokens.semantic.warning,
          bad: t.tokens.semantic.danger,
        }[tone];
        const fill = {
          brand: t.tokens.brand.primarySoft,
          neutral: t.tokens.surface.sunken,
          good: t.tokens.surface.sunken,
          warning: t.tokens.surface.sunken,
          bad: t.tokens.surface.sunken,
        }[tone];
        // Small markers have no room for a ring and an icon inside it: a
        // toned step is a solid dot with its icon cut out of it, a neutral
        // one a thin empty ring.
        if (dense) {
          const solid = tone !== 'neutral' && !upcoming;
          return {
            width: size,
            height: size,
            flexShrink: 0,
            boxSizing: 'border-box',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: t.tokens.text.inverse,
            bgcolor: solid ? ink : t.tokens.surface.raised,
            border: solid ? 'none' : `1.5px ${upcoming ? 'dashed' : 'solid'} ${upcoming ? t.tokens.border.strong : ink}`,
            '& svg': { fontSize: size - 6, display: 'block' },
          };
        }
        return {
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: ink,
          bgcolor: upcoming ? t.tokens.surface.raised : fill,
          border: `2px ${upcoming ? 'dashed' : 'solid'} ${upcoming ? t.tokens.border.strong : ink}`,
          '& svg': { fontSize: 14 },
        };
      }}
    >
      {typeof children === 'string' ? (
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
}
